import React, { useEffect, useState } from "react";
import {
	Card,
	CardActionArea,
	CardContent,
	CardMedia,
	Typography,
	Button,
	Box,
	SpeedDial,
	SpeedDialAction,
	Tooltip,
	Alert,
	Snackbar,
	CircularProgress,
} from "@mui/material";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import "./ProductCard.css";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useCart } from "../../context/cartContext";
import { useSite } from "../../context/siteContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import customHttp from "../../services/customHTTP";
import { ASSETS_BASE_URL, RSPL_LIST } from "../../config";

const ProductCard = ({
	product,
	machineID,
	onAddToCart,
	onViewDetails,
	onSaveForLater,
	onDelete,
	showDial,
	showSavedForLaterDial,
}) => {
	const rsplMapping = Object.fromEntries(RSPL_LIST.flatMap(({ key, value }) => key.map((k) => [k, value])));
	const { addToCart } = useCart();
	const { selectedSite, selectedSiteCurrency, currencySymbol } = useSite();
	const { user } = useAuth();
	const [imageSrc, setImageSrc] = useState(`${ASSETS_BASE_URL}/stock.png`);
	const [isLoading, setIsLoading] = useState(false); // To manage the loading state
	const [isSuccess, setIsSuccess] = useState(false); // To track success state
	const [alertisSuccess, setAlertIsSuccess] = useState(false); // To track success state
	const [alertMessage, setAlertMessage] = useState(""); // To track success state
	const [openAlert, setOpenAlert] = useState(false); // To control the alert visibility
	const navigate = useNavigate();
	const [price, setPrice] = useState(0);
	useEffect(() => {
		const checkImageExists = async () => {
			const productImageUrl = `${ASSETS_BASE_URL}/ProductImages/TMB/${product.product_number}.png`;
			try {
				const response = await fetch(productImageUrl, { method: "HEAD" });
				if (response.ok) {
					setImageSrc(productImageUrl);
				}
			} catch (error) {
				console.warn("Product image not found, using default image.");
			}
		};

		checkImageExists();
		// eslint-disable-next-line
	}, [product.product_number]);

	useEffect(() => {
		if (product && selectedSiteCurrency) {
			const key = `price_${selectedSiteCurrency}`;
			let currencyPrice = product[key];
			if (currencyPrice === "0") {
				currencyPrice = product.product_sell_price;
			}
			const finalPrice = product.purchasable ? currencyPrice : 0;

			setPrice(finalPrice);
		}
	}, [product, selectedSiteCurrency]);

	const handleViewProduct = (productid) => {
		navigate(`/machines/${machineID}/${productid}`);
	};

	const handleDeleteClick = (event) => {
		event.stopPropagation();
		console.log(machineID);
		onDelete(product.product_id, machineID);
	};

	const handleAddToRequest = async (event) => {
		event.stopPropagation();

		// Start the loading state and reset success state
		setIsLoading(true);
		setIsSuccess(false);
		setOpenAlert(false);
		try {
			// Call the addToCart function (which you've defined to add the product)
			// Set success state
			const req_body = {
				machine_id: machineID,
				product_id: product.product_id,
				site_number: selectedSite,
				user_id: user.user_id,
			};

			try {
				const response = await customHttp.post("/request_list/saverequestlist", req_body);
				if (response.data.success) {
					setIsSuccess(true);
					setIsLoading(false);
					setOpenAlert(true);
					setAlertIsSuccess(true);
					setAlertMessage(`Product ${product.product_number} added to Request List`);

					setTimeout(() => {
						setIsLoading(false);
						setIsSuccess(false);
						setOpenAlert(false);
					}, 1000);
				} else if (!response.data.success) {
					setOpenAlert(true);
					setAlertIsSuccess(false);
					setAlertMessage(`Product ${product.product_number} already in Request List`);
				}
			} catch (error) {
				console.error("Error fetching product name:", error);
			}

			// Reset the button text back to "Add to Cart" after 2 seconds
			// 2 seconds delay
		} catch (error) {
			// Handle any error (optional)
			setIsSuccess(false);
			setOpenAlert(true); // Show error alert if desired
		} finally {
			setIsLoading(false);
		}
	};

	const handleSavedForLater = async (event) => {
		event.stopPropagation();

		setOpenAlert(false);
		try {
			// Call the addToCart function (which you've defined to add the product)
			// Set success state
			const req_body = {
				machine_id: machineID,
				product_id: product.product_id,
				site_number: selectedSite,
				user_id: user.user_id,
			};

			try {
				const response = await customHttp.post("/saved_for_later/saveproduct", req_body);
				if (response.data.success) {
					setOpenAlert(true);
					setAlertIsSuccess(true);
					setAlertMessage(`Product ${product.product_number} added to Saved for Later`);

					setTimeout(() => {
						setOpenAlert(false);
					}, 1000);
				} else if (!response.data.success) {
					setOpenAlert(true);
					setAlertIsSuccess(false);
					setAlertMessage(`Product ${product.product_number} already in Saved for Later`);
				}
			} catch (error) {
				console.error("Error fetching product name:", error);
			}

			// Reset the button text back to "Add to Cart" after 2 seconds
			// 2 seconds delay
		} catch (error) {
			setOpenAlert(true); // Show error alert if desired
		}
	};

	const handleAddToCart = async (event) => {
		event.stopPropagation();

		// Start the loading state and reset success state
		setIsLoading(true);
		setIsSuccess(false);
		setOpenAlert(false);
		try {
			// Call the addToCart function (which you've defined to add the product)
			await addToCart({ ProductID: product.product_id, Quantity: 1, Site: selectedSite });

			// Set success state
			setIsSuccess(true);
			setAlertIsSuccess(true);
			setAlertMessage(`Product ${product.product_number} Added to the Cart`);
			setOpenAlert(true); // Show success alert
			// Reset the button text back to "Add to Cart" after 2 seconds
			setTimeout(() => {
				setIsLoading(false);
				setIsSuccess(false);
				setOpenAlert(false);
			}, 2000); // 2 seconds delay
		} catch (error) {
			// Handle any error (optional)
			setIsSuccess(false);
			setOpenAlert(true); // Show error alert if desired
		} finally {
			// Stop the loading spinner after a delay and reset the button state
			setIsLoading(false);
		}
	};

	return (
		<>
			<Card className="product-card">
				<CardActionArea className="product-card-content" onClick={() => handleViewProduct(product.product_id)}>
					<CardMedia
						component="img"
						image={imageSrc}
						alt={product.product_name}
						sx={{ objectFit: "cover" }}
						className="product-img"
					/>
					<CardContent sx={{ paddingBottom: "0px", textAlign: "center" }}>
						<Tooltip title={product.product_name}>
							<Typography
								variant="body1"
								sx={{
									fontWeight: "bold",
									lineHeight: "1.2rem",
									marginBottom: "5px",
									display: "-webkit-box",
									overflow: "hidden",
									WebkitLineClamp: 2,
									WebkitBoxOrient: "vertical",
									textOverflow: "ellipsis",
									minHeight: "2.4rem",
								}}>
								{product.product_name}
							</Typography>
						</Tooltip>
						<Tooltip title={product.product_name}>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									fontSize: "11px",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
									maxWidth: "150px",
								}}>
								<b>Part #:</b> {product.product_number}
							</Typography>
						</Tooltip>
						<Tooltip title={product.product_rspl_code}>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									fontSize: "11px",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
									maxWidth: "150px",
								}}>
								<b>Part Type:</b>{" "}
								{rsplMapping[product.product_rspl_code]
									? rsplMapping[product.product_rspl_code]
									: "Other"}
							</Typography>
						</Tooltip>
						<Box mt={1} display="flex" alignItems="center" justifyContent="center" minHeight="25px">
							<Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "14px" }}>
								{price === "0.00" || price === 0
									? ""
									: `${currencySymbol}${
											typeof price === "string" ? parseFloat(price).toFixed(2) : price.toFixed(2)
									  }`}
							</Typography>
						</Box>
					</CardContent>

					{/* SpeedDial with Save and Delete Actions */}
					{showDial ? (
						<>
							<SpeedDial
								ariaLabel="Product actions"
								icon={<MoreHorizIcon variant="Outlined" sx={{ color: "primary.main", fontSize: 24 }} />}
								direction="down"
								className="speed-dial-custom"
								sx={{
									// Inline css needed here
									width: "10px",
									height: "10px",
									marginTop: -1,
									marginRight: 0,
									boxShadow: "none",
									backgroundColor: "transparent",
									"& .MuiFab-primary": {
										backgroundColor: "transparent",
										boxShadow: "none",
										"&:hover": {
											backgroundColor: "transparent",
										},
									},
								}}>
								<SpeedDialAction
									icon={<DeleteIcon />}
									tooltipTitle="Delete"
									onClick={handleDeleteClick}
								/>
								{product.purchasable === 1 && (
									<SpeedDialAction
										icon={<BookmarkAddIcon />}
										tooltipTitle="Save for Later"
										onClick={handleSavedForLater}
									/>
								)}
							</SpeedDial>
						</>
					) : (
						<></>
					)}

					{showSavedForLaterDial && product.purchasable === 1 ? (
						<>
							<SpeedDial
								ariaLabel="Product actions"
								icon={<MoreHorizIcon variant="Outlined" sx={{ color: "primary.main", fontSize: 24 }} />}
								direction="down"
								className="speed-dial-custom"
								sx={{
									// Inline css needed here
									width: "10px",
									height: "10px",
									marginTop: -1,
									marginRight: 0,
									boxShadow: "none",
									backgroundColor: "transparent",
									"& .MuiFab-primary": {
										backgroundColor: "transparent",
										boxShadow: "none",
										"&:hover": {
											backgroundColor: "transparent",
										},
									},
								}}>
								<SpeedDialAction
									icon={<BookmarkAddIcon />}
									tooltipTitle="Save for Later"
									onClick={handleSavedForLater}
								/>
							</SpeedDial>
						</>
					) : (
						<></>
					)}

					<div className="atc-button">
						{product.purchasable === 1 ? (
							<Button
								size="small"
								variant="contained"
								sx={{
									backgroundColor: "var(--primary-main)",
									padding: "8px 15px",
									fontWeight: "bolder",
								}}
								onClick={handleAddToCart}
								disabled={isLoading} // Disable the button while loading or after success
							>
								{isLoading ? (
									<>
										<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
										Adding...
									</>
								) : isSuccess ? (
									<>
										<CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
										Added
									</>
								) : (
									"Add to Cart"
								)}
							</Button>
						) : (
							<Button
								size="small"
								variant="contained"
								sx={{
									backgroundColor: "var(--primary-main)",
									padding: "8px 15px",
									fontWeight: "bolder",
								}}
								onClick={handleAddToRequest}
								disabled={isLoading} // Disable the button while loading or after success
							>
								{isLoading ? (
									<>
										<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
										Adding...
									</>
								) : isSuccess ? (
									<>
										<CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
										Added
									</>
								) : (
									"Add to Request List"
								)}
							</Button>
						)}
					</div>
				</CardActionArea>
			</Card>
			<Snackbar
				open={openAlert}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				autoHideDuration={3000}
				onClose={() => setOpenAlert(false)}
				className="SnackBar">
				<Alert severity={alertisSuccess ? "success" : "error"} sx={{ width: "100%" }}>
					{alertMessage}
				</Alert>
			</Snackbar>
		</>
	);
};

export default ProductCard;
